import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Headphones, HeartHandshake, Link2, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { avatarSrc } from "@/lib/defaults";
import { PRAYER_ACCENT, fill, timeAgo } from "@/lib/prayers";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { ChainCounters, URGENT_COLOR, useChainLive } from "./PrayerChain";
import { PrayerLinkShare } from "./PrayerLinkShare";
import { VoicePrayerPlayer } from "./VoicePrayerPlayer";

// « 2 min 30 s » / « 45 s » — minutes entières + secondes des prières écourtées.
export const fmtDuration = (seconds, t) => {
  const s = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(s / 60), r = s % 60;
  const parts = [];
  if (m) parts.push(fill(t("prayers.chain_dur_min", "{m} min"), { m }));
  if (r || !m) parts.push(fill(t("prayers.chain_dur_sec", "{s} s"), { s: r }));
  return parts.join(" ");
};

// Bouton « Remercier (n) » de l'auteur : notifie chaque maillon pas encore remercié (+ un mot public la 1re fois).
export const ChainThankButton = ({ p, toThank, onThanked, className = "" }) => {
  const { t } = useApp();
  const [busy, setBusy] = useState(false);
  const thank = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await api.post(`/prayers/${p.id}/chain/thanks`);
      if (data.thanked > 0) toast.success(fill(t("prayers.chain_thank_sent", "Merci envoyé à {n} personne(s) 🙏"), { n: data.thanked }));
      else toast.info(t("prayers.chain_thank_none", "Tout le monde a déjà été remercié 🙏"));
      onThanked?.(data);
    } catch (e2) { toast.error(e2?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  const done = !toThank;
  return (
    <Button size="sm" variant="outline" className={`rounded-full ${className}`} disabled={busy || done} onClick={thank}
      style={done ? undefined : { borderColor: PRAYER_ACCENT, color: PRAYER_ACCENT }} data-testid={`prayer-chain-thank-${p.id}`} data-pending={toThank || 0}>
      {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : done ? <Check size={14} className="mr-1" /> : <HeartHandshake size={14} className="mr-1" />}
      {done ? t("prayers.chain_thanked", "Merci envoyé ✓") : fill(t("prayers.chain_thank_n", "Remercier ({n})"), { n: toThank })}
    </Button>
  );
};

const ParticipantRow = ({ u, t, lang, onNav, prayerId, onVoiceRemoved }) => (
  <li className="py-2 flex flex-col gap-1.5" data-testid={`prayer-chain-participant-${u.id}`} data-seconds={u.seconds} data-voices={(u.audios || []).length}>
    <div className="flex items-center gap-2.5">
      <Avatar className="h-9 w-9"><AvatarImage src={avatarSrc(u)} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
      <div className="min-w-0 flex-1">
        <Link to={`/profile/${u.id}`} className="text-sm font-semibold flex items-center gap-1 hover:underline truncate" onClick={onNav}>
          {u.name} <VerifiedBadge user={u} size={12} />
        </Link>
        <div className="text-[11px] flex items-center gap-1 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
          <span className="font-semibold" style={{ color: PRAYER_ACCENT }}>{fmtDuration(u.seconds, t)}</span>
          {u.sessions > 1 && <span>· {fill(t("prayers.chain_times_n", "{n} fois"), { n: u.sessions })}</span>}
          <span>· {timeAgo(u.last_at, lang)}</span>
          {u.audios?.length > 0 && <span className="inline-flex items-center gap-0.5 font-semibold" style={{ color: URGENT_COLOR }}><Headphones size={10} /> {t("prayers.voice_badge", "Vocal")}</span>}
        </div>
      </div>
      {u.thanked && <span className="chip text-[10px]" data-testid={`prayer-chain-participant-thanked-${u.id}`}><Check size={10} className="inline mr-0.5" /> {t("prayers.chain_thanked_badge", "Remercié(e)")}</span>}
    </div>
    {/* Lot 657 : prières vocales de ce maillon — l'auteur seul les reçoit */}
    {(u.audios || []).map((a) => <VoicePrayerPlayer key={a.session_id} prayerId={prayerId} audio={a} lang={lang} onRemoved={onVoiceRemoved} />)}
  </li>
);

// Liste des maillons de la chaîne (minutes + prières écourtées) ; l'auteur peut remercier tout le monde d'un tap.
export const ChainPeopleDialog = ({ p, open, onOpenChange, onThanked }) => {
  const { t, lang } = useApp();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get(`/prayers/${p.id}/chain/participants`).then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  }, [open, p.id]);
  const items = data?.items || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="bottom" className="sm:max-w-sm" data-testid={`prayer-chain-people-dialog-${p.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Link2 size={16} color={URGENT_COLOR} /> {t("prayers.chain_people_title", "Ils ont prié pour cette demande")}</DialogTitle>
          <DialogDescription asChild><div><ChainCounters chain={data || p.chain} compact testId={`prayer-chain-people-counters-${p.id}`} /></div></DialogDescription>
        </DialogHeader>
        <DialogBody>
          {data === null && <div className="py-6 grid place-items-center"><Loader2 className="animate-spin" style={{ color: PRAYER_ACCENT }} /></div>}
          {data && items.length === 0 && (
            <div className="py-4 text-center space-y-3" data-testid={`prayer-chain-people-empty-${p.id}`}>
              <span className="mx-auto h-12 w-12 rounded-full grid place-items-center" style={{ background: URGENT_COLOR + "1f" }}><Users size={22} color={URGENT_COLOR} /></span>
              <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("prayers.chain_people_empty", "Personne n'a encore prié 1 minute. Partage ta demande pour lancer la chaîne.")}</p>
              {p.is_mine && <PrayerLinkShare p={p} />}
            </div>
          )}
          {items.length > 0 && (
            <ul className="divide-y" style={{ borderColor: "var(--yalola-border)" }} data-testid={`prayer-chain-people-list-${p.id}`} data-count={items.length}>
              {items.map((u) => <ParticipantRow key={u.id} u={u} t={t} lang={lang} prayerId={p.id} onNav={() => onOpenChange(false)}
                onVoiceRemoved={(sid, voices) => setData((s) => s && { ...s, voices: voices ?? s.voices, items: s.items.map((x) => ({ ...x, audios: (x.audios || []).filter((a) => a.session_id !== sid) })) })} />)}
            </ul>
          )}
        </DialogBody>
        {p.is_mine && items.length > 0 && (
          <DialogFooter>
            <ChainThankButton p={p} toThank={data?.to_thank} className="w-full sm:w-auto" onThanked={(d) => { setData((s) => s && { ...s, to_thank: 0, items: s.items.map((u) => ({ ...u, thanked: true })) }); onThanked?.(d); }} />
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// « Voir qui a prié (n) » → liste ; `autoOpen` quand on arrive depuis la notification « X a prié pour toi ».
export const ChainPeopleButton = ({ p, onThanked, autoOpen = false, size = "sm", className = "" }) => {
  const { t } = useApp();
  const [open, setOpen] = useState(!!autoOpen);
  const n = p.chain?.people || 0;
  const voices = p.chain?.voices || 0;
  return (
    <>
      <Button size={size} className={`rounded-full font-semibold ${className}`} style={{ background: URGENT_COLOR, color: "#fff" }} onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid={`prayer-chain-people-${p.id}`} data-count={n} data-voices={voices}>
        <Users size={14} className="mr-1" /> {t("prayers.chain_people_btn", "Voir qui a prié")}{n > 0 ? ` (${n})` : ""}
        {voices > 0 && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-white/20 px-1.5 text-[11px]"><Headphones size={11} /> {voices}</span>}
      </Button>
      <ChainPeopleDialog p={p} open={open} onOpenChange={setOpen} onThanked={onThanked} />
    </>
  );
};

// Bloc de l'auteur sur sa demande urgente : il ne prie pas pour lui-même — compteurs en direct, qui a prié, merci.
export const ChainAuthorPanel = ({ p, onPatch, openPeople = false }) => {
  const { t } = useApp();
  const [live] = useChainLive(p.id, true, p.chain);
  const chain = live ? { ...p.chain, ...live } : p.chain;
  const onThanked = (d) => onPatch?.(p.id, { chain: { ...chain, to_thank: 0 }, ...(d?.comment_count != null ? { comment_count: d.comment_count } : {}) });
  return (
    <div className="space-y-2" data-testid={`prayer-chain-author-${p.id}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide inline-flex items-center gap-1" style={{ color: URGENT_COLOR }}><Link2 size={11} /> {t("prayers.chain_mine_title", "Ta chaîne de prière")}</div>
      <ChainCounters chain={chain} testId={`prayer-chain-counters-${p.id}`} />
      <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("prayers.chain_mine_hint", "La communauté porte ta demande — partage-la pour agrandir la chaîne.")}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <ChainPeopleButton p={{ ...p, chain }} autoOpen={openPeople} onThanked={onThanked} />
        {chain?.people > 0 && <ChainThankButton p={p} toThank={chain?.to_thank} onThanked={onThanked} />}
      </div>
    </div>
  );
};
